import React, { useEffect, useState, useRef, Fragment } from 'react';
import { ArrowLeft, Package, Barcode, CheckCircle2, AlertCircle, Truck, User, MapPin, Phone, CreditCard, Info, XCircle, ChevronRight, Scan, Clock, Globe, Smartphone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Order, OrderStatus, PaymentType, HistoryEvent } from './OrderQueue';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
interface OrderDetailsProps {
  order: Order;
  onBack: () => void;
  onUpdateStatus: (status: OrderStatus, updates?: Partial<Order>) => void;
  onUpdatePaymentMethod?: (paymentType: PaymentType) => void;
  readOnly?: boolean;
  initialShowRejectModal?: boolean;
  initialIsBulkScanning?: boolean;
  initialConfirmItem?: LineItem | null;
  initialShowPartialModal?: boolean;
  initialScanningItem?: LineItem | null;
  initialShowGlobalScanModal?: boolean;
  initialShowHoldModal?: boolean;
  initialShowOtpModal?: boolean;
}
interface LotNumber {
  number: string;
  expiryDate: string;
  stock: number;
}
interface LineItem {
  id: string;
  name: string;
  sku: string;
  barcode: string;
  image: string;
  quantity: number;
  pickedQuantity: number;
  isSerialized: boolean;
  serialNumbers: string[];
  status: 'Pending' | 'Picked' | 'Partially Picked' | 'Unavailable';
  lotNumbers?: LotNumber[];
  selectedLotNumber?: string;
  requiresPrescription?: boolean;
}
export function OrderDetails({
  order,
  onBack,
  onUpdateStatus,
  initialShowRejectModal = false,
  initialIsBulkScanning = false,
  initialConfirmItem = null,
  initialShowPartialModal = false,
  initialScanningItem = null,
  initialShowGlobalScanModal = false,
  initialShowHoldModal = false,
  initialShowOtpModal = false,
  readOnly = false,
  onUpdatePaymentMethod
}: OrderDetailsProps) {
  const [isBulkScanning, setIsBulkScanning] = useState(initialIsBulkScanning);
  const [isEditingPayment, setIsEditingPayment] = useState(false);
  const [isWaitingConfirmation, setIsWaitingConfirmation] = useState(false);
  const customerConfirmationTimerRef = useRef<number | null>(null);
  const [items, setItems] = useState<LineItem[]>([{
    id: '1',
    name: 'Panadol Extra 500mg',
    sku: 'PAN-001',
    barcode: '6281234567890',
    image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=200&h=200&fit=crop',
    quantity: 2,
    pickedQuantity: 0,
    isSerialized: false,
    serialNumbers: [],
    status: 'Pending',
    requiresPrescription: true,
    lotNumbers: [{
      number: 'LOT-A1',
      expiryDate: '2025-12',
      stock: 50
    }, {
      number: 'LOT-A2',
      expiryDate: '2026-06',
      stock: 30
    }]
  }, {
    id: '2',
    name: 'Amoxicillin 500mg (Serialized)',
    sku: 'AMX-500',
    barcode: '6289876543210',
    image: 'https://images.unsplash.com/photo-1576602976047-174e57a47881?w=200&h=200&fit=crop',
    quantity: 1,
    pickedQuantity: 0,
    isSerialized: true,
    serialNumbers: [],
    status: 'Pending',
    requiresPrescription: true,
    lotNumbers: [{
      number: 'LOT-B1',
      expiryDate: '2025-08',
      stock: 100
    }]
  }, {
    id: '3',
    name: 'Vitamin C 1000mg Effervescent',
    sku: 'VIT-C',
    barcode: '6285554443332',
    image: 'https://images.unsplash.com/photo-1616671285442-299c27666291?w=200&h=200&fit=crop',
    quantity: 1,
    pickedQuantity: 0,
    isSerialized: false,
    serialNumbers: [],
    status: 'Pending',
    lotNumbers: [{
      number: 'LOT-C1',
      expiryDate: '2024-11',
      stock: 10
    }, {
      number: 'LOT-C2',
      expiryDate: '2025-03',
      stock: 45
    }]
  }]);
  const [scanningItem, setScanningItem] = useState<LineItem | null>(null);
  const [showGlobalScanModal, setShowGlobalScanModal] = useState(initialShowGlobalScanModal);
  const [globalScanValue, setGlobalScanValue] = useState('');
  const [manualQty, setManualQty] = useState('');
  const [tempSerial, setTempSerial] = useState('');
  const [tempLot, setTempLot] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(initialShowRejectModal);
  const [showPartialModal, setShowPartialModal] = useState(initialShowPartialModal);
  const [showHoldModal, setShowHoldModal] = useState(initialShowHoldModal);
  const [showOtpModal, setShowOtpModal] = useState(initialShowOtpModal);
  const [showRxModal, setShowRxModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showReturnReceiptModal, setShowReturnReceiptModal] = useState(false);
  const [returnItemStatus, setReturnItemStatus] = useState<Record<string, {
    received: boolean;
    condition: 'Good' | 'Damaged' | 'Missing';
    note: string;
    missingQuantity: number;
  }>>({});
  const [returnReason, setReturnReason] = useState('');
  const [returnMethod, setReturnMethod] = useState<'Driver' | 'Customer'>('Customer');
  const [otpValue, setOtpValue] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  useEffect(() => {
    let interval: any;
    if (showOtpModal && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [showOtpModal, resendTimer]);
  const handleResendOtp = () => {
    setResendTimer(60);
    setCanResend(false);
    import('sonner').then(({
      toast
    }) => toast.success('OTP has been resent to the customer'));
  };
  const handleSendWhatsApp = () => {
    import('sonner').then(({
      toast
    }) => toast.success('OTP sent via WhatsApp successfully'));
  };
  const [rejectionReason, setRejectionReason] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  useEffect(() => {
    setShowRejectModal(initialShowRejectModal);
  }, [initialShowRejectModal]);
  useEffect(() => {
    setIsBulkScanning(initialIsBulkScanning);
  }, [initialIsBulkScanning]);
  useEffect(() => {
    setShowPartialModal(initialShowPartialModal);
  }, [initialShowPartialModal]);
  useEffect(() => {
    setShowGlobalScanModal(initialShowGlobalScanModal);
  }, [initialShowGlobalScanModal]);
  useEffect(() => {
    setShowHoldModal(initialShowHoldModal);
  }, [initialShowHoldModal]);
  useEffect(() => {
    setShowOtpModal(initialShowOtpModal);
  }, [initialShowOtpModal]);
  useEffect(() => {
    if (initialScanningItem) {
      setScanningItem(initialScanningItem);
      setShowGlobalScanModal(true);
      setManualQty(initialScanningItem.pickedQuantity > 0 ? initialScanningItem.pickedQuantity.toString() : initialScanningItem.quantity.toString());
    }
  }, [initialScanningItem]);
  useEffect(() => {
    return () => {
      if (customerConfirmationTimerRef.current !== null) {
        window.clearTimeout(customerConfirmationTimerRef.current);
      }
    };
  }, []);
  const handleConfirmPartial = () => {
    setShowPartialModal(false);
    setIsWaitingConfirmation(true);
    if (customerConfirmationTimerRef.current !== null) {
      window.clearTimeout(customerConfirmationTimerRef.current);
    }
    customerConfirmationTimerRef.current = window.setTimeout(() => {
      setIsWaitingConfirmation(false);
      customerConfirmationTimerRef.current = null;
      onUpdateStatus(order.isPickup ? 'Ready for Pickup' : 'Searching for Driver');
    }, 5000);
  };
  const rejectionReasons = ['Out of Stock', 'Prescription Invalid', 'Controlled Substance Restriction', 'Store Closing Soon', 'Delivery Area Not Covered', 'Other'];
  const handlePickItem = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    setScanningItem(item);
    setShowGlobalScanModal(true);
    setManualQty(item.pickedQuantity > 0 ? item.pickedQuantity.toString() : item.quantity.toString());
    setTempLot(item.selectedLotNumber || '');
  };
  const handleGlobalScan = (input: string) => {
    const parts = input.split('*');
    const scanValue = parts[0].trim();
    const scanQty = parts.length > 1 ? parseInt(parts[1]) : 1;
    const item = items.find(i => i.sku === scanValue || i.isSerialized && scanValue.startsWith('SN-') || i.lotNumbers && i.lotNumbers.some(l => l.number === scanValue));
    if (item) {
      setScanningItem(item);
      setManualQty(item.pickedQuantity > 0 ? Math.min(item.pickedQuantity + scanQty, item.quantity).toString() : Math.min(scanQty, item.quantity).toString());
      if (item.isSerialized && scanValue.startsWith('SN-')) {
        setTempSerial(scanValue);
      }
      if (item.lotNumbers?.some(l => l.number === scanValue)) {
        setTempLot(scanValue);
      }
      import('sonner').then(({
        toast
      }) => {
        toast.success(`Item Identified: ${item.name}`);
      });
    } else {
      import('sonner').then(({
        toast
      }) => {
        toast.error(`Item ${scanValue} not found in this order`);
      });
    }
    setGlobalScanValue('');
  };
  const handleConfirmItemFulfillment = () => {
    if (!scanningItem) return;
    const qty = parseInt(manualQty) || 0;
    const finalQty = Math.min(qty, scanningItem.quantity);
    setItems(prev => prev.map(i => i.id === scanningItem.id ? {
      ...i,
      pickedQuantity: finalQty,
      selectedLotNumber: tempLot,
      serialNumbers: tempSerial && i.isSerialized && !i.serialNumbers.includes(tempSerial) ? [...i.serialNumbers, tempSerial] : i.serialNumbers,
      status: finalQty === i.quantity ? 'Picked' : finalQty > 0 ? 'Partially Picked' : 'Pending'
    } : i));
    // If we are in bulk scan mode, we stay in the scan modal but clear the current item
    if (isBulkScanning) {
      setScanningItem(null);
      setTempSerial('');
      setTempLot('');
      setManualQty('');
      // showGlobalScanModal remains true
    } else {
      setScanningItem(null);
      setShowGlobalScanModal(false);
      setTempSerial('');
      setTempLot('');
      setManualQty('');
    }
  };
  const allPicked = items.every(i => i.status === 'Picked' || i.status === 'Unavailable');
  return <div className="max-w-6xl mx-auto space-y-6 pb-20" data-id="element-33" data-mp="3sudakugs3uev">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-34" data-mp="1h7jj39l3lcuc">
        <div className="flex items-center gap-4" data-id="element-35" data-mp="1h94e10bbqxsq">
          <button onClick={onBack} className="p-2 hover:bg-white rounded-full transition-colors border border-transparent hover:border-slate-200" data-id="element-36" data-mp="1d5oh02vcevfi">
            <ArrowLeft className="w-5 h-5 text-slate-600" data-id="element-37" data-mp="138a1c5h8uao9" />
          </button>
          <div data-id="element-38" data-mp="13a4l3ggs3wqr">
            <div className="flex items-center gap-3" data-id="element-39" data-mp="20vsia3l26j3c">
              <h2 className="text-2xl font-bold text-slate-900" data-id="element-40" data-mp="20v92aseyy4ry">
                Order #{order.id}
              </h2>
              <div className={cn('px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border', order.paymentType === 'COD' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-emerald-50 text-emerald-700 border-emerald-100')} data-id="element-41" data-mp="1vysdrx3uxoec">
                {order.paymentType}
              </div>
            </div>
            <div className="flex items-center gap-2 mt-0.5" data-id="element-42" data-mp="cy9mvmvs97qm">
              <p className="text-slate-500 text-xs" data-id="element-43" data-mp="cyt38z5sbo2j">
                Placed on {order.createdAt}
              </p>
            </div>
          </div>
        </div>

        {!readOnly && <div className="flex items-center gap-3" data-id="element-44" data-mp="37opg2rui99a">
            {['Pending', 'Accepted', 'Picking', 'Searching for Driver', 'Driver Assigned', 'Ready for Pickup', 'Out for Delivery', 'Delivery Attempt Failed'].includes(order.status) && <div className="flex items-center gap-2" data-id="element-45" data-mp="3gfi2e031fvi6">
                <button onClick={() => setShowRejectModal(true)} className="px-4 py-2 text-red-600 font-bold text-sm hover:bg-red-50 rounded-xl transition-colors" data-id="element-46" data-mp="3gh2wjolfxcfi">
                  Cancel Order
                </button>
                <button onClick={() => setShowHoldModal(true)} className="px-4 py-2 text-slate-600 font-bold text-sm hover:bg-slate-50 rounded-xl transition-colors" data-id="element-47" data-mp="1jly4g6a76lng">
                  Hold Order
                </button>
              </div>}

            {order.status === 'Held' && <div className="flex gap-3" data-id="element-48" data-mp="1xs8fisf3sek1">
                <button onClick={() => setShowRejectModal(true)} className="px-4 py-2 text-red-600 font-bold text-sm hover:bg-red-50 rounded-xl transition-colors" data-id="element-49" data-mp="1xqf83awo1qaz">
                  Cancel Order
                </button>
                <button onClick={() => onUpdateStatus(order.previousStatus || 'Pending')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all" data-id="element-50" data-mp="2lhug9kb4j1u5">
                  Resume Order
                </button>
              </div>}

            {order.status === 'Pending' && <div className="flex items-center gap-3" data-id="element-51" data-mp="ol7vxj002gv0">
                {order.requiresPrescription && <button onClick={() => setShowRxModal(true)} className="px-6 py-2 bg-purple-600 text-white font-bold rounded-xl shadow-lg shadow-purple-200 hover:bg-purple-700 transition-all flex items-center gap-2" data-id="element-52" data-mp="tkaz0xwf6d0c">
                    <Globe className="w-4 h-4" data-id="element-53" data-mp="5u6wktuywjax" />
                    View Prescription
                  </button>}
                <button onClick={() => onUpdateStatus('Accepted')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all" data-id="element-54" data-mp="etaj67tcfgu6">
                  Accept Order
                </button>
              </div>}

            {order.status === 'Accepted' && <button onClick={() => onUpdateStatus('Picking')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2" data-id="element-55" data-mp="3n70yd6h2ncr7">
                <Package className="w-4 h-4" data-id="element-56" data-mp="35qdo4lz4kxuy" />
                Start Picking
              </button>}

            {order.status === 'Picking' && <>
                {allPicked ? <button onClick={() => {
            if (order.isPickup) {
              onUpdateStatus('Ready for Pickup');
            } else {
              onUpdateStatus('Searching for Driver');
            }
          }} className="px-6 py-2 bg-emerald-600 text-white font-bold rounded-xl shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all flex items-center gap-2" data-id="element-57" data-mp="2wsbe5gxeyuvj">
                    <CheckCircle2 className="w-4 h-4" data-id="element-58" data-mp="291o3wp00zt5t" />
                    Ready to Pick
                  </button> : <button onClick={() => setShowPartialModal(true)} className="px-6 py-2 bg-amber-500 text-white font-bold rounded-xl shadow-lg shadow-amber-200 hover:bg-amber-600 transition-all flex items-center gap-2" data-id="element-59" data-mp="23f5h46tnebxj">
                    <CheckCircle2 className="w-4 h-4" data-id="element-60" data-mp="1ugjsbtxl20ob" />
                    Confirm Scan
                  </button>}
              </>}

            {order.status === 'Searching for Driver' && <button onClick={() => onUpdateStatus('Driver Assigned', {
          courierPhone: order.courierName ? '+966 9200 12345' : undefined,
          driverInfo: {
            name: 'Ahmed Al-Fahad',
            phone: '+966 55 000 1111',
            vehiclePlate: 'ABC 1234',
            rating: 4.8,
            avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Ahmed'
          }
        })} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2" data-id="element-61" data-mp="28ei1mkx1r6wy">
                <Truck className="w-4 h-4" data-id="element-62" data-mp="bfhpcjmjhpuh" />
                Simulate Driver Assignment
              </button>}

            {order.status === 'Driver Assigned' && <button onClick={() => onUpdateStatus('Out for Delivery')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2" data-id="element-63" data-mp="6j14byamzrlm">
                <Truck className="w-4 h-4" data-id="element-64" data-mp="1vu1kgsmee918" />
                Driver Picked Up
              </button>}

            {order.status === 'Out for Delivery' && <div className="flex gap-3" data-id="element-65" data-mp="2azfihykw0k6j">
                <button onClick={() => onUpdateStatus('Delivery Attempt Failed')} className="px-4 py-2 text-rose-600 font-bold text-sm hover:bg-rose-50 rounded-xl transition-colors" data-id="element-66" data-mp="2awji83lkhjex">
                  Delivery Failed
                </button>
                <button onClick={() => onUpdateStatus('Delivered')} className="px-6 py-2 bg-emerald-600 text-white font-bold rounded-xl shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all" data-id="element-67" data-mp="2orc7htr9edyp">
                  Confirm Delivery
                </button>
              </div>}

            {order.status === 'Ready for Pickup' && <button onClick={() => setShowOtpModal(true)} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2" data-id="element-68" data-mp="37msqidcke3ge">
                <User className="w-4 h-4" data-id="element-69" data-mp="15sehrs008eht" />
                Customer Pickup
              </button>}
          </div>}

        <div className="flex items-center gap-3" data-id="element-70" data-mp="10yjqo3i2kx8p">
          {order.status === 'Delivered' && <button onClick={() => setShowReturnModal(true)} className="px-6 py-2 bg-rose-600 text-white font-bold rounded-xl shadow-lg shadow-rose-200 hover:bg-rose-700 transition-all" data-id="element-71" data-mp="vzgw5czpu9ez">
              Initiate Return
            </button>}

          {order.status === 'Return Requested' && <button onClick={() => onUpdateStatus('Refunded')} className="px-6 py-2 bg-slate-800 text-white font-bold rounded-xl shadow-lg shadow-slate-200 hover:bg-slate-900 transition-all" data-id="element-72" data-mp="1fp10ob8rsvh7">
              Complete Refund
            </button>}

          {order.status === 'Waiting Return Confirmation' && <button onClick={() => {
          // Initialize return status for items
          const initialStatus: any = {};
          items.forEach(item => {
            initialStatus[item.id] = {
              received: true,
              condition: 'Good',
              note: '',
              missingQuantity: 0
            };
          });
          setReturnItemStatus(initialStatus);
          setShowReturnReceiptModal(true);
        }} className="px-6 py-2 bg-emerald-600 text-white font-bold rounded-xl shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all" data-id="element-73" data-mp="3viriyddzrsye">
              Confirm Item Return
            </button>}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6" data-id="element-74" data-mp="30tdyjnd1d3zg">
        <div className="col-span-2 space-y-6" data-id="element-75" data-mp="1ubqw7lcuwcc8">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-76" data-mp="1ucin4ividlw6">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between" data-id="element-77" data-mp="1patfld7hvqcu">
              <div className="flex items-center gap-4" data-id="element-78" data-mp="1f6cyyhm6y57z">
                <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-79" data-mp="1k82d464pmz0a">
                  <Package className="w-4 h-4 text-blue-600" data-id="element-80" data-mp="1p63wyt9yn2ct" />
                  Order Items ({items.length})
                </h3>
                {order.status === 'Picking' && <button onClick={() => {
                setShowGlobalScanModal(true);
                setIsBulkScanning(true);
              }} className={cn('flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold transition-all', isBulkScanning ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')} data-id="element-81" data-mp="1xz73ehm6uexy">
                    <Scan className="w-3.5 h-3.5" data-id="element-82" data-mp="ii6wa08txmwc" />
                    {isBulkScanning ? 'Bulk Scan Active' : 'Enable Bulk Scan'}
                  </button>}
              </div>
              {order.status === 'Picking' && <div className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded" data-id="element-83" data-mp="kfya37lva9w0">
                  {items.filter(i => i.status === 'Picked').length} /{' '}
                  {items.length} Fully Picked
                </div>}
            </div>

            <div className="divide-y divide-slate-100" data-id="element-84" data-mp="tdqswlqsrzo5">
              {items.map(item => <div key={item.id} className={cn('p-6 flex items-center justify-between group transition-colors', item.status === 'Picked' && 'bg-emerald-50/30')} data-id="element-85" data-mp="of619apm2obb">
                  <div className="flex gap-4" data-id="element-86" data-mp="1c6uoicnksmle">
                    <div className="relative" data-id="element-87" data-mp="185j7gvirp9ot">
                      <img src={item.image} alt={item.name} className={cn('w-16 h-16 rounded-xl object-cover border', item.status === 'Picked' ? 'border-emerald-200' : 'border-slate-200')} data-id="element-88" data-mp="186uagt4e2dev" />
                      {item.status === 'Picked' && <div className="absolute -top-2 -right-2 bg-emerald-500 text-white rounded-full p-0.5 border-2 border-white" data-id="element-89" data-mp="181r21416eetk">
                          <CheckCircle2 className="w-3.5 h-3.5" data-id="element-90" data-mp="1cvlk6314ulqj" />
                        </div>}
                    </div>
                    <div data-id="element-91" data-mp="1337hn828db3b">
                      <p className="font-bold text-slate-900" data-id="element-92" data-mp="134ilt8zl8kuy">{item.name}</p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1" data-id="element-93" data-mp="p3o8cbxontpf">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-94" data-mp="u3keagpxxv7m">
                          SKU:{' '}
                          <span className="text-slate-600 font-mono" data-id="element-95" data-mp="1wfxf2x91xb1a">
                            {item.sku}
                          </span>
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-96" data-mp="1nnbz1owt1ig5">
                          Barcode:{' '}
                          <span className="text-slate-600 font-mono" data-id="element-97" data-mp="1ioscbmae02mq">
                            {item.barcode}
                          </span>
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-98" data-mp="18q32mgsn9ohk">
                          Qty:{' '}
                          <span className="text-blue-600" data-id="element-99" data-mp="14r41z8dy4dvx">
                            {item.pickedQuantity} / {item.quantity}
                          </span>
                        </p>
                        {item.isSerialized && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-bold uppercase border border-blue-100" data-id="element-100" data-mp="2u3nwtovpsyb7">
                            <Barcode className="w-3 h-3" data-id="element-101" data-mp="36mtdprc4n6kk" />
                            Serialized
                          </div>}
                        {item.requiresPrescription && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[10px] font-bold uppercase border border-purple-100" data-id="element-102" data-mp="3gccw6vvxgyew">
                            <Info className="w-3 h-3" data-id="element-103" data-mp="2ir74zx4444nf" />
                            Rx Required
                          </div>}
                        {item.selectedLotNumber && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[10px] font-bold uppercase border border-amber-100" data-id="element-104" data-mp="2wondde5yy8ht">
                            <Info className="w-3 h-3" data-id="element-105" data-mp="7wji0np2jz10" />
                            Lot: {item.selectedLotNumber}
                          </div>}
                      </div>
                      {item.serialNumbers.length > 0 && <div className="flex flex-wrap gap-1 mt-2" data-id="element-106" data-mp="240qx82e7g1mq">
                          {item.serialNumbers.map(sn => <p key={sn} className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100" data-id="element-107" data-mp="2hz73js85nq4c">
                              SN: {sn}
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {order.status === 'Picking' && <button onClick={() => handlePickItem(item.id)} className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-bold hover:border-blue-500 hover:text-blue-600 transition-all flex items-center gap-2" data-id="element-108" data-mp="1u8l6evzovq94">
                      {item.isSerialized ? <Scan className="w-4 h-4" data-id="element-109" data-mp="3hzj0r3hyl6sx" /> : <CheckCircle2 className="w-4 h-4" data-id="element-110" data-mp="3i1n85vf2gnxy" />}
                      {item.status === 'Picked' ? 'Edit' : item.isSerialized ? 'Scan Serial' : 'Confirm Qty'}
                    </button>}
                </div>)}
            </div>
          </div>

          {/* Branch Routing Flow */}
          {order.routingHistory && order.routingHistory.length > 0 && <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 shadow-sm" data-id="element-111" data-mp="3rxgjxckyodeo">
              <h3 className="text-xs font-black text-blue-400 uppercase tracking-widest mb-4 flex items-center gap-2" data-id="element-112" data-mp="2bjgp2g0z3dya">
                <Globe className="w-4 h-4" data-id="element-113" data-mp="2gj31wocw2s6k" />
                Branch Routing Flow
              </h3>
              <div className="flex items-center gap-4 overflow-x-auto pb-2" data-id="element-114" data-mp="386ny5zcadmxo">
                <div className="flex items-center shrink-0" data-id="element-115" data-mp="3371lbr0de8pe">
                  <div className="flex flex-col items-center gap-2" data-id="element-116" data-mp="335grk3lvqtsl">
                    <div className="w-10 h-10 rounded-full bg-white border-2 border-blue-200 flex items-center justify-center text-blue-600 shadow-sm" data-id="element-117" data-mp="2y75i3tzhylaw">
                      <MapPin className="w-5 h-5" data-id="element-118" data-mp="2tb89yaye0l97" />
                    </div>
                    <div className="text-center" data-id="element-119" data-mp="3qvujlu4gssn6">
                      <p className="text-xs font-bold text-slate-900" data-id="element-120" data-mp="3lwzrg1klxrqe">
                        Original Branch
                      </p>
                      <p className="text-[10px] text-slate-400 font-medium" data-id="element-121" data-mp="3h58a8hnccycx">
                        Innova Main
                      </p>
                    </div>
                  </div>
                </div>

                {order.routingHistory.map((route, idx) => <Fragment key={idx} data-id="element-122" data-mp="8qr9tw3jzsgf" data-mpi={idx}>
                    <div className="flex flex-col items-center" data-id="element-123" data-mp="8pojpqe21u1k">
                      <ChevronRight className="w-5 h-5 text-blue-300" data-id="element-124" data-mp="h0oxtogagkzo" />
                    </div>
                    <div className="flex items-center shrink-0" data-id="element-125" data-mp="l0h0jkj03wx4">
                      <div className="flex flex-col items-center gap-2" data-id="element-126" data-mp="q0bq9kr1iggl">
                        <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200" data-id="element-127" data-mp="q2eogs2cjfwe">
                          <MapPin className="w-5 h-5" data-id="element-128" data-mp="zz15f5rjxmt5" />
                        </div>
                        <div className="text-center" data-id="element-129" data-mp="13zuy0cfxvpsd">
                          <p className="text-xs font-bold text-slate-900" data-id="element-130" data-mp="13ya1iha2dwrv">
                            {route.branchName}
                          </p>
                          <p className="text-[10px] text-blue-500 font-bold uppercase" data-id="element-131" data-mp="1dqfpzh13xw2b">
                            {route.timestamp.split(',')[1]}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Fragment>)}
              </div>
            </div>}

          {/* Audit Trail */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-132" data-mp="2uetb8o8m9x3d">
            <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-133" data-mp="2ucgpdzzqn2l1">
              <Clock className="w-4 h-4 text-blue-600" data-id="element-134" data-mp="2gh4tvqdrd832" />
              Full Order Audit Trail
            </h3>
            <div className="space-y-6 relative before:absolute before:left-[19px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-100" data-id="element-135" data-mp="2kiglu2k3u5ef">
              {order.history?.map((event, idx) => <div key={event.id} className="relative pl-12" data-id="element-136" data-mp="3d75d79vcywcz">
                  <div className="absolute left-0 top-0 w-10 h-10 rounded-full bg-white border-2 border-slate-100 flex items-center justify-center z-10" data-id="element-137" data-mp="2zdeg50ytd4y5">
                    <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-138" data-mp="3r5c3o4xz9u2m" />
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-139" data-mp="3vvk5e0hewgfd">
                    <div className="flex justify-between items-start" data-id="element-140" data-mp="1kgtod1g2dz3h">
                      <div data-id="element-141" data-mp="1kf0gxjxmnauf">
                        <p className="text-sm font-bold text-slate-900" data-id="element-142" data-mp="1fhqft70vlaqr">
                          {event.status}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-143" data-mp="1u5ldl1tqq5ap">
                          {event.message}
                        </p>
                      </div>
                      <p className="text-[10px] font-bold text-slate-400" data-id="element-144" data-mp="11hz4je1qu8gg">
                        {event.timestamp}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        <div className="space-y-6" data-id="element-145" data-mp="1be2a6tvt0p85">
          {/* Order Source Details */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-146" data-mp="16fh7mnt2xtgj">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-147" data-mp="2mtz9nc19qrjm">
              <Globe className="w-4 h-4 text-blue-600" data-id="element-148" data-mp="2hx0f4sdp0ikw" />
              Order Source
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-149" data-mp="2p06z3lki36vd">
              <div data-id="element-150" data-mp="2p1rufden2tua">
                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-151" data-mp="2ty8hs5e0l46b">
                  Source
                </p>
                <div className="flex items-center gap-2" data-id="element-152" data-mp="2yymrrsz6eo6z">
                  {order.channel === 'Website' ? <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-md text-[10px] font-bold uppercase" data-id="element-153" data-mp="33vd9ekqmprui">
                      <Globe className="w-3 h-3" data-id="element-154" data-mp="2b80la64tlbz3" />
                      Website
                    </div> : <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-50 text-orange-700 rounded-md text-[10px] font-bold uppercase" data-id="element-155" data-mp="2g6twtt0jqswm">
                      <Smartphone className="w-3 h-3" data-id="element-156" data-mp="2kzwtwb4j8khk" />
                      Aggregator
                    </div>}
                </div>
              </div>
              <div data-id="element-157" data-mp="1sg80v03tgz2z">
                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-158" data-mp="1sfogzdulw0mb">
                  Source ID
                </p>
                <p className="text-sm font-bold text-slate-900" data-id="element-159" data-mp="5l2vcv02hdrd">
                  {order.sourceOrderId || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-160" data-mp="3rssyiydas6xf">
            <div className="flex items-center justify-between mb-4" data-id="element-161" data-mp="3mtgesmhq3fz2">
              <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-162" data-mp="3murjcoizsrr8">
                <User className="w-4 h-4 text-blue-600" data-id="element-163" data-mp="3i0x1zrqv0ovb" />
                Customer Profile
              </h3>
              {order.isLoyaltyCustomer && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px] font-black uppercase tracking-widest border border-amber-200" data-id="element-164" data-mp="392bav87bo1ix">
                  Loyalty Member
                </span>}
            </div>
            <div className="space-y-4" data-id="element-165" data-mp="2z445lmmeb7wr">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100" data-id="element-166" data-mp="130h9clyk5537">
                <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-167" data-mp="17ztwz8x96g6u">
                  <User className="w-5 h-5 text-slate-400" data-id="element-168" data-mp="ta4hs9sbsbnk" />
                </div>
                <div data-id="element-169" data-mp="y86bdondntda">
                  <p className="text-sm font-bold text-slate-900" data-id="element-170" data-mp="y7o4ly339wr4">
                    {order.customerName}
                  </p>
                  <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider" data-id="element-171" data-mp="p8sy0kw4ukna">
                    {order.customerGender || 'N/A'} •{' '}
                    {order.customerAge || 'N/A'} Years
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-172" data-mp="fcppmxaotprw">
                <div className="space-y-1" data-id="element-173" data-mp="24qmfed7nskbs">
                  <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-174" data-mp="24ngtf6ubtyka">
                    City
                  </p>
                  <p className="text-xs font-bold text-slate-700" data-id="element-175" data-mp="3u6f89yezwtmj">
                    {order.customerCity || 'N/A'}
                  </p>
                </div>
                <div className="space-y-1" data-id="element-176" data-mp="3p9f0nj2hzitb">
                  <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-177" data-mp="7rm68ksg7jdj">
                    District
                  </p>
                  <p className="text-xs font-bold text-slate-700" data-id="element-178" data-mp="2zm02alsbu8i">
                    {order.customerDistrict || 'N/A'}
                  </p>
                </div>
              </div>

              <div className="space-y-1 pt-2 border-t border-slate-50" data-id="element-179" data-mp="cp5ijf5l5m2u">
                <p className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1" data-id="element-180" data-mp="qpq0k93ofdva">
                  <MapPin className="w-3 h-3" data-id="element-181" data-mp="lqdg1v0a2ivv" /> Address
                </p>
                <p className="text-xs text-slate-600 leading-relaxed" data-id="element-182" data-mp="lp2e80qe7l7e">
                  {order.customerAddress}
                </p>
              </div>

              <div className="space-y-1" data-id="element-183" data-mp="2njjr7y8y58ze">
                <p className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1" data-id="element-184" data-mp="2nkl4m9dfq7ka">
                  <Phone className="w-3 h-3" data-id="element-185" data-mp="we6s3dqe8gu3" /> Phone
                </p>
                <p className="text-xs font-bold text-blue-600" data-id="element-186" data-mp="wegkt92tp23h">
                  {order.customerPhone}
                </p>
              </div>
            </div>
          </div>

          {/* Shipping & Courier Details */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-187" data-mp="15cih5bbm36kf">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-188" data-mp="1abvbsi8q5hxk">
              <Truck className="w-4 h-4 text-blue-600" data-id="element-189" data-mp="1fbhn2m4zso3q" />
              Shipping Details
            </h3>
            <div className="space-y-4" data-id="element-190" data-mp="1k4klb7kpslq9">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-3" data-id="element-191" data-mp="1o54igb5afpce">
                <div className="flex justify-between items-center" data-id="element-192" data-mp="1t4pi6nsa81q6">
                  <span className="text-xs font-bold text-slate-500 uppercase" data-id="element-193" data-mp="1t2cr9l4jqh0z">
                    Method
                  </span>
                  <span className="text-sm font-black text-blue-600 uppercase tracking-tight" data-id="element-194" data-mp="2877h4236ti5g">
                    {order.isPickup ? 'Self Pickup' : 'Home Delivery'}
                  </span>
                </div>
                {!readOnly && !order.isPickup && ['Pending', 'Accepted', 'Picking', 'Searching for Driver'].includes(order.status) && <button onClick={() => onUpdateStatus(order.status, {
                isPickup: true,
                otp: order.otp || '5566',
                courierName: undefined,
                courierPhone: undefined,
                driverInfo: undefined
              })} className="w-full py-2.5 bg-white border border-blue-200 text-blue-700 rounded-xl text-xs font-black hover:bg-blue-50 transition-colors flex items-center justify-center gap-2" data-id="element-195" data-mp="2lyvoxt6elkgm">
                      <User className="w-4 h-4" data-id="element-196" data-mp="3tdjxclivp9sq" />
                      Change to Self Pickup
                    </button>}
              </div>

              {!order.isPickup && <div className="space-y-4" data-id="element-197" data-mp="3jlvxbw89akfn">
                  <div className="flex justify-between items-center" data-id="element-198" data-mp="3jkkxu8lufyuc">
                    <span className="text-xs font-bold text-slate-400 uppercase" data-id="element-199" data-mp="b86w0vh9xue6">
                      Courier
                    </span>
                    <div className="flex flex-col items-end gap-1" data-id="element-200" data-mp="g3m8bizmvi8h">
                      <div className="flex items-center gap-2" data-id="element-201" data-mp="18680w8gmq3n">
                        <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center" data-id="element-202" data-mp="66zq71yoz5a5">
                          <Globe className="w-3 h-3 text-slate-500" data-id="element-203" data-mp="2lyngwfn441ki" />
                        </div>
                        <span className="text-sm font-bold text-slate-900" data-id="element-204" data-mp="2qy9uwrarllud">
                          {order.courierName || 'Not Assigned'}
                        </span>
                      </div>
                      {order.courierPhone && <div className="flex items-center gap-1.5 text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md" data-id="element-205" data-mp="143egkd3sqdq1">
                          <Phone className="w-3 h-3" data-id="element-206" data-mp="1dyydn2u3jigd" />
                          {order.courierPhone}
                        </div>}
                    </div>
                  </div>

                  {order.driverInfo && <div className="pt-4 border-t border-slate-100" data-id="element-207" data-mp="lcnabkf3v3fv">
                      <p className="text-[10px] font-bold text-slate-400 uppercase mb-3" data-id="element-208" data-mp="z38v5eo4dnge">
                        Assigned Driver
                      </p>
                      <div className="flex items-center gap-3" data-id="element-209" data-mp="6lwkuj0zkgfx">
                        <img src={order.driverInfo.avatar} alt={order.driverInfo.name} className="w-10 h-10 rounded-full border-2 border-white shadow-sm" data-id="element-210" data-mp="6kczwf4dxh7t" />
                        <div className="flex-1" data-id="element-211" data-mp="6uv852puanb3">
                          <p className="text-sm font-bold text-slate-900" data-id="element-212" data-mp="6u3jkbuntpub">
                            {order.driverInfo.name}
                          </p>
                          <p className="text-[10px] text-slate-500 font-mono" data-id="element-213" data-mp="gs9o717upa2v">
                            {order.driverInfo.vehiclePlate}
                          </p>
                        </div>
                        <button className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors" data-id="element-214" data-mp="3ka1aq037x1to">
                          <Phone className="w-4 h-4" data-id="element-215" data-mp="2tjn7ahwuq98" />
                        </button>
                      </div>
                    </div>}
                </div>}
            </div>
          </div>

          {/* Payment & Loyalty */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-216" data-mp="31ja3p56pdnig">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-217" data-mp="1h0zlpfwy23ic">
              <CreditCard className="w-4 h-4 text-blue-600" data-id="element-218" data-mp="1gywl61y60rzd" />
              Payment & Loyalty
            </h3>
            <div className="space-y-4" data-id="element-219" data-mp="172jwjihh94bh">
              {/* Main Totals */}
              <div className="space-y-2" data-id="element-220" data-mp="170guu16ypmqx">
                <div className="flex justify-between text-sm" data-id="element-221" data-mp="1c13hpgo8yc2s">
                  <span className="text-slate-500" data-id="element-222" data-mp="1bza7xsic7bqk">Subtotal</span>
                  <span className="font-medium text-slate-900" data-id="element-223" data-mp="1zsikfajxqvek">
                    {((order.totalAmount + (order.discountAmount || 0)) / 1.15).toFixed(2)}{' '}
                    SAR
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-224" data-mp="1uqt8lsow2dpf">
                  <span className="text-slate-500" data-id="element-225" data-mp="fauj5yn1x6ei">VAT (15%)</span>
                  <span className="font-medium text-slate-900" data-id="element-226" data-mp="fdqgpluzlt2f">
                    {((order.totalAmount + (order.discountAmount || 0)) * 0.15 / 1.15).toFixed(2)}{' '}
                    SAR
                  </span>
                </div>
              </div>

              {/* Discounts & Loyalty Breakdown */}
              {(order.discountAmount || order.loyaltyPointsUsed || order.qitafAmount || order.walletAmount) && <div className="p-3 bg-emerald-50/50 rounded-xl border border-emerald-100 space-y-2" data-id="element-227" data-mp="239wkpd49rcfa">
                  {order.discountAmount && <div className="flex justify-between text-xs" data-id="element-228" data-mp="1taz2erxfsf4q">
                      <div className="flex items-center gap-2" data-id="element-229" data-mp="1tb7dcea01ada">
                        <span className="text-emerald-700 font-bold uppercase" data-id="element-230" data-mp="1ocxiy5w3mdsq">
                          Coupon
                        </span>
                        <span className="px-1.5 py-0.5 bg-white text-emerald-700 rounded text-[9px] font-black border border-emerald-200 uppercase" data-id="element-231" data-mp="3nmvtukvvpkzs">
                          {order.couponCode}
                        </span>
                      </div>
                      <span className="font-bold text-emerald-700" data-id="element-232" data-mp="3v7fx9m813d2r">
                        -{order.discountAmount.toFixed(2)} SAR
                      </span>
                    </div>}
                  {order.loyaltyPointsUsed && <div className="flex justify-between text-xs" data-id="element-233" data-mp="8ur4l625fu1f">
                      <span className="text-emerald-700 font-bold uppercase" data-id="element-234" data-mp="389mhv4tv920y">
                        Loyalty Points ({order.loyaltyPointsUsed})
                      </span>
                      <span className="font-bold text-emerald-700" data-id="element-235" data-mp="3h8ghl42hu8iq">
                        Redeemed
                      </span>
                    </div>}
                  {order.qitafAmount && <div className="flex justify-between text-xs" data-id="element-236" data-mp="whpm8720980x">
                      <span className="text-emerald-700 font-bold uppercase" data-id="element-237" data-mp="11h2ej7bnb7aw">
                        Qitaf Points
                      </span>
                      <span className="font-bold text-emerald-700" data-id="element-238" data-mp="2aesk3drin2ga">
                        -{order.qitafAmount.toFixed(2)} SAR
                      </span>
                    </div>}
                  {order.walletAmount && <div className="flex justify-between text-xs" data-id="element-239" data-mp="2egdu8rco12ms">
                      <span className="text-emerald-700 font-bold uppercase" data-id="element-240" data-mp="2ei8avtgwm8l2">
                        Wallet Balance
                      </span>
                      <span className="font-bold text-emerald-700" data-id="element-241" data-mp="2of39ecd6g8zc">
                        -{order.walletAmount.toFixed(2)} SAR
                      </span>
                    </div>}
                </div>}

              <div className="pt-3 border-t border-slate-100 flex justify-between items-center" data-id="element-242" data-mp="2y4wklc9eqm32">
                <span className="font-bold text-slate-900" data-id="element-243" data-mp="3c45opdkc9pqo">Grand Total</span>
                <span className="text-lg font-black text-blue-600" data-id="element-244" data-mp="376476x2k9yhb">
                  {order.totalAmount.toFixed(2)} SAR
                </span>
              </div>

              {/* Payment Method Selector */}
              <div className="mt-4 space-y-3" data-id="element-245" data-mp="3vp5y1q4vydfa">
                <div className={cn('p-3 rounded-xl flex items-center justify-between border', order.paymentType === 'COD' ? 'bg-amber-50 border-amber-100' : 'bg-blue-50 border-blue-100')} data-id="element-246" data-mp="3gsnkvzd85jdj">
                  <div className="flex items-center gap-2" data-id="element-247" data-mp="3crvah2sm98fo">
                    <Info className={cn('w-4 h-4', order.paymentType === 'COD' ? 'text-amber-600' : 'text-blue-600')} data-id="element-248" data-mp="2y1xjyg77s6nb" />
                    <div className="flex flex-col" data-id="element-249" data-mp="2p3litk8q1qgm">
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter" data-id="element-250" data-mp="2t1rm2iacpnws">
                        Payment Method
                      </span>
                      <span className={cn('text-xs font-black uppercase', order.paymentType === 'COD' ? 'text-amber-700' : 'text-blue-700')} data-id="element-251" data-mp="usvigwjr7fv3">
                        {order.paymentType}
                      </span>
                    </div>
                  </div>
                  {!readOnly && !['Out for Delivery', 'Delivered', 'Delivery Attempt Failed'].includes(order.status) && <button onClick={() => setIsEditingPayment(!isEditingPayment)} className="px-3 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-black text-blue-600 hover:border-blue-500 uppercase shadow-sm transition-all" data-id="element-252" data-mp="3tb12oh0tzbrv">
                        {isEditingPayment ? 'Cancel' : 'Change'}
                      </button>}
                </div>

                {isEditingPayment && <div className="grid grid-cols-2 gap-2 p-2 bg-slate-50 rounded-xl border border-slate-100 animate-in fade-in slide-in-from-top-1" data-id="element-253" data-mp="2dq8ne0ehxarb">
                    {['COD', 'Visa', 'Mada', 'ApplePay', 'Tamara', 'Tabby'].map(method => <button key={method} onClick={() => {
                  onUpdatePaymentMethod?.(method as PaymentType);
                  setIsEditingPayment(false);
                }} className={cn('px-3 py-2 rounded-lg text-[10px] font-bold uppercase transition-all', order.paymentType === method ? 'bg-blue-600 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-blue-300')} data-id="element-254" data-mp="2sf4plgtvftk0">
                          {method}
                        </button>)}
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence data-id="element-255" data-mp="2m0z61vcx12cc">
        {showGlobalScanModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-256" data-mp="2m2ipfutvbtic">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-257" data-mp="2zy4axs0w7v60">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-258" data-mp="1x7mix757ec8z">
                <h3 className="font-bold text-slate-900" data-id="element-259" data-mp="1j8d5ugcany98">
                  {isBulkScanning ? 'Continuous Picking' : scanningItem ? 'Confirm Quantity' : 'Scan Item'}
                </h3>
                <button onClick={() => {
              setShowGlobalScanModal(false);
              setIsBulkScanning(false);
              setScanningItem(null);
            }} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-260" data-mp="2byvfy1bfqzw5">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-261" data-mp="273plge3ywcwg" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-262" data-mp="2zmd2nifuf7s9">
                {scanningItem ? <div className="space-y-6" data-id="element-263" data-mp="2zog4czqcypct">
                    <div className="flex flex-col items-center text-center space-y-3" data-id="element-264" data-mp="5hrna3jjzaxp">
                      <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600" data-id="element-265" data-mp="afjcuqwq6kyq">
                        <Package className="w-8 h-8" data-id="element-266" data-mp="f7ucdsnk04qr" />
                      </div>
                      <div data-id="element-267" data-mp="j8wdkf116qvd">
                        <h4 className="font-bold text-xl text-slate-900" data-id="element-268" data-mp="j7383325mcoy">
                          {scanningItem.name}
                        </h4>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-269" data-mp="3nml6abrlhzxp">
                          SKU: {scanningItem.sku}
                        </p>
                      </div>
                      <div className="flex items-center gap-8 pt-2" data-id="element-270" data-mp="hn425r9g23s">
                        <div className="text-center" data-id="element-271" data-mp="fsh6lwfhzx2">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-272" data-mp="16yqous5pc7di">
                            Ordered
                          </p>
                          <p className="text-lg font-black text-slate-900" data-id="element-273" data-mp="1m0pgdjol2ht0">
                            {scanningItem.quantity}
                          </p>
                        </div>
                        <div className="text-center" data-id="element-274" data-mp="1h48jo13biv4b">
                          <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest" data-id="element-275" data-mp="1c9urs8nv764e">
                            Picked
                          </p>
                          <p className="text-lg font-black text-emerald-600" data-id="element-276" data-mp="13b0ro8bbrxm3">
                            {scanningItem.pickedQuantity}
                          </p>
                        </div>
                        <div className="text-center" data-id="element-277" data-mp="tbrznl00st2d">
                          <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest" data-id="element-278" data-mp="tee4vdzfuwhf">
                            Remaining
                          </p>
                          <p className="text-lg font-black text-blue-600" data-id="element-279" data-mp="2np1d1nvosypw">
                            {scanningItem.quantity - scanningItem.pickedQuantity}
                          </p>
                        </div>
                      </div>
                    </div>

                    {scanningItem.lotNumbers && <div className="space-y-3" data-id="element-280" data-mp="81dytcyav8e2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-281" data-mp="82p2zdvnqi5p">
                          Select Lot Number
                        </label>
                        <div className="grid gap-2" data-id="element-282" data-mp="ztmqm3j63ckb">
                          {scanningItem.lotNumbers.map(lot => <button key={lot.number} onClick={() => setTempLot(lot.number)} className={cn('p-4 rounded-2xl border-2 text-left transition-all flex justify-between items-center', tempLot === lot.number ? 'border-blue-600 bg-blue-50/50' : 'border-slate-100 hover:border-slate-200')} data-id="element-283" data-mp="14r4sj9yrorit">
                              <div data-id="element-284" data-mp="33xjx4eap976z">
                                <p className="text-sm font-black text-slate-900" data-id="element-285" data-mp="340o57mracn2x">
                                  {lot.number}
                                </p>
                                <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-286" data-mp="3d0l0i786kk6y">
                                  EXP: {lot.expiryDate}
                                </p>
                              </div>
                              <div className="text-right" data-id="element-287" data-mp="2kcfdtexkgl4h">
                                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest" data-id="element-288" data-mp="2yzs0x830uywz">
                                  Stock
                                </p>
                                <p className="text-sm font-black text-slate-600" data-id="element-289" data-mp="26io15xojgl4j">
                                  {lot.stock}
                                </p>
                              </div>
                            </button>)}
                        </div>
                      </div>}

                    <div className="space-y-3" data-id="element-290" data-mp="19scb5bz5wral">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-291" data-mp="1dtny19qniyf3">
                        Confirm Picked Quantity
                      </label>
                      <div className="flex items-center gap-4" data-id="element-292" data-mp="1nj7lytt81igt">
                        <button onClick={() => setManualQty(Math.max(0, parseInt(manualQty || '0') - 1).toString())} className="w-14 h-14 rounded-2xl border-2 border-slate-100 flex items-center justify-center text-2xl font-black text-slate-400 hover:bg-slate-50 transition-colors" data-id="element-293" data-mp="1nipj3ec205zx">
                          -
                        </button>
                        <div className="flex-1 relative" data-id="element-294" data-mp="3r044nwul4rkb">
                          <input type="number" value={manualQty} onChange={e => setManualQty(e.target.value)} className="w-full text-center py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none font-black text-2xl text-slate-900 focus:border-blue-500 transition-all" data-id="element-295" data-mp="3qykp68gr6kjl" />
                        </div>
                        <button onClick={() => setManualQty(Math.min(scanningItem.quantity, parseInt(manualQty || '0') + 1).toString())} className="w-14 h-14 rounded-2xl border-2 border-slate-100 flex items-center justify-center text-2xl font-black text-slate-400 hover:bg-slate-50 transition-colors" data-id="element-296" data-mp="9jdwgt6wy6e4">
                          +
                        </button>
                      </div>
                      {parseInt(manualQty) < scanningItem.quantity && <p className="text-[10px] font-bold text-amber-600 flex items-center gap-1.5" data-id="element-297" data-mp="2lr1anrd5zhz7">
                          <AlertCircle className="w-3 h-3" data-id="element-298" data-mp="2804wv6nwd5c7" />
                          Partial fulfillment:{' '}
                          {scanningItem.quantity - parseInt(manualQty)} units
                          pending
                        </p>}
                    </div>

                    <div className="flex gap-4 pt-4" data-id="element-299" data-mp="232mnjf5ywo3o">
                      <button onClick={() => {
                  setScanningItem(null);
                  setTempLot('');
                  setManualQty('');
                }} className="flex-1 py-4 text-slate-500 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-300" data-mp="1p2bty4d9wvet">
                        Back to Scan
                      </button>
                      <button disabled={scanningItem.lotNumbers && scanningItem.lotNumbers.length > 0 && !tempLot} onClick={() => {
                  handleConfirmItemFulfillment();
                }} className="flex-[1.5] py-4 bg-blue-400 text-white font-black rounded-2xl shadow-lg shadow-blue-100 hover:bg-blue-500 transition-all disabled:opacity-50" data-id="element-301" data-mp="1jhw1gif2zth2">
                        Confirm & Next Scan
                      </button>
                    </div>
                  </div> : <div className="space-y-8" data-id="element-302" data-mp="274axnsq2w0xs">
                    <div className="text-center space-y-2" data-id="element-303" data-mp="3ehjl2lcxm1sz">
                      <div className="w-20 h-20 bg-blue-50 rounded-3xl flex items-center justify-center mx-auto text-blue-600" data-id="element-304" data-mp="3jho3igxeh6lu">
                        <Scan className="w-10 h-10" data-id="element-305" data-mp="3nj18ypwkz7s8" />
                      </div>
                      <h4 className="font-bold text-xl text-slate-900" data-id="element-306" data-mp="3si433fb0vtlf">
                        Ready to Scan
                      </h4>
                      <p className="text-sm text-slate-500" data-id="element-307" data-mp="2vtel7ozeb0zg">
                        Scan an item's barcode or serial number to begin
                        fulfillment.
                      </p>
                    </div>

                    <div className="space-y-2" data-id="element-308" data-mp="34rgussx731yc">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-309" data-mp="39k1nfr4d790a">
                        Scan Item
                      </label>
                      <div className="relative" data-id="element-310" data-mp="2c29txna9q09x">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2" data-id="element-311" data-mp="2h25s35waaxhk">
                          <Scan className="w-5 h-5 text-blue-400" data-id="element-312" data-mp="29jy5gee5p9q2" />
                        </div>
                        <input autoFocus type="text" value={globalScanValue} onChange={e => setGlobalScanValue(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleGlobalScan(globalScanValue);
                    }
                  }} placeholder="Scan barcode, SKU, or Lot..." className="w-full pl-12 pr-4 py-5 bg-white border-2 border-blue-500 rounded-2xl outline-none shadow-[0_0_0_4px_rgba(59,130,246,0.1)] font-mono font-bold text-slate-700 placeholder:text-slate-300 transition-all" data-id="element-313" data-mp="24kbpvyauvhe3" />
                      </div>
                    </div>

                    <div className="flex gap-4 pt-4" data-id="element-314" data-mp="12w17vu8boadt">
                      <button onClick={() => {
                  setShowGlobalScanModal(false);
                  setIsBulkScanning(false);
                }} className="w-full py-4 text-slate-500 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-315" data-mp="3nxgwydagitla">
                        Cancel & Close
                      </button>
                    </div>
                  </div>}
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Cancel Order Modal */}
      <AnimatePresence data-id="element-316" data-mp="2w72owxp4kbhj">
        {showRejectModal && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-317" data-mp="2w3nalx39z6h6">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-318" data-mp="2m7k46f1e4lof">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-rose-50/60" data-id="element-319" data-mp="2m22u8whakm3x">
                <div className="flex items-center gap-3" data-id="element-320" data-mp="1oeubh98odr5m">
                  <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center" data-id="element-321" data-mp="1oc7s8ccf9n7k">
                    <XCircle className="w-6 h-6" data-id="element-322" data-mp="1tb2oafzeh89m" />
                  </div>
                  <div data-id="element-323" data-mp="1yb72fzcu5qwo">
                    <h3 className="font-bold text-slate-900" data-id="element-324" data-mp="1yann8qeklgmc">Cancel Order</h3>
                    <p className="text-[10px] font-bold text-rose-600 uppercase tracking-widest" data-id="element-325" data-mp="234ji3jzjenda">
                      Reason required
                    </p>
                  </div>
                </div>
                <button onClick={() => {
              setShowRejectModal(false);
              setRejectionReason('');
              setCustomReason('');
            }} className="p-1 rounded-full hover:bg-white transition-colors" aria-label="Close cancellation dialog" data-id="element-326" data-mp="1akt985iilm0i">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-327" data-mp="17dzo71q74n1d" />
                </button>
              </div>

              <div className="p-6 space-y-4" data-id="element-328" data-mp="jo5c4mdpb2i6">
                <p className="text-sm text-slate-600" data-id="element-329" data-mp="eoakggm3q8w2">
                  Select why this order is being cancelled. This will be saved
                  in the order history.
                </p>
                <div className="space-y-2" data-id="element-330" data-mp="omgj8pcm2ywp">
                  {rejectionReasons.map(reason => <button key={reason} onClick={() => {
                setRejectionReason(reason);
                if (reason !== 'Other') setCustomReason('');
              }} className={cn('w-full px-4 py-3 rounded-xl border-2 text-left text-sm font-bold transition-all', rejectionReason === reason ? 'border-rose-600 bg-rose-50 text-rose-700' : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200')} data-id="element-331" data-mp="250ou3mo0rjsi">
                      {reason}
                    </button>)}
                </div>

                {rejectionReason === 'Other' && <textarea value={customReason} onChange={event => setCustomReason(event.target.value)} placeholder="Enter the cancellation reason..." className="w-full min-h-[88px] p-4 rounded-xl border-2 border-slate-100 bg-slate-50 text-sm outline-none focus:border-rose-500 focus:bg-white transition-colors" data-id="element-332" data-mp="2hjkerjssmonq" />}
              </div>

              <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3" data-id="element-333" data-mp="1qegflicgwen3">
                <button onClick={() => {
              setShowRejectModal(false);
              setRejectionReason('');
              setCustomReason('');
            }} className="flex-1 py-3.5 rounded-xl text-slate-600 font-bold hover:bg-white transition-colors" data-id="element-334" data-mp="1ld0y209i0q9g">
                  Go Back
                </button>
                <button disabled={!rejectionReason || rejectionReason === 'Other' && !customReason.trim()} onClick={() => {
              const reason = rejectionReason === 'Other' ? customReason.trim() : rejectionReason;
              onUpdateStatus('Cancelled', {
                rejectionReason: reason
              });
              setShowRejectModal(false);
              setRejectionReason('');
              setCustomReason('');
            }} className="flex-1 py-3.5 rounded-xl bg-rose-600 text-white font-bold shadow-lg shadow-rose-100 hover:bg-rose-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-335" data-mp="245bk6ifxo9kv">
                  Confirm Cancel
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Partial Fulfillment Modal */}
      <AnimatePresence data-id="element-336" data-mp="129fhbtwzj3qb">
        {showPartialModal && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-337" data-mp="129z0tf2aa06g">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-[32px] w-full max-w-lg overflow-hidden shadow-2xl" data-id="element-338" data-mp="16clqpafe9b10">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-339" data-mp="3m2en5vk2bgmp">
                <h3 className="font-bold text-slate-900" data-id="element-340" data-mp="3hao7da2cw0nw">
                  Partial Fulfillment
                </h3>
                <button onClick={() => setShowPartialModal(false)} className="p-1 hover:bg-slate-100 rounded-full transition-colors" data-id="element-341" data-mp="3v1t8kgldh10c">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-342" data-mp="8vwdlwyel04e" />
                </button>
              </div>

              <div className="p-8 space-y-8" data-id="element-343" data-mp="3w0as11fzeqf">
                <div className="text-center space-y-4" data-id="element-344" data-mp="3u6zu9jsq8cm">
                  <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto text-amber-500" data-id="element-345" data-mp="2anspj7ohgbzp">
                    <AlertCircle className="w-8 h-8" data-id="element-346" data-mp="25pheitmgbvfw" />
                  </div>
                  <div className="space-y-2" data-id="element-347" data-mp="l6x4zcc01w7z">
                    <h4 className="text-xl font-black text-slate-900" data-id="element-348" data-mp="l7yjxrw4z2uz">
                      Items Still Pending
                    </h4>
                    <p className="text-sm text-slate-500 font-medium max-w-[280px] mx-auto" data-id="element-349" data-mp="bgklzoju2b3y">
                      Some items in this order have not been fully scanned or
                      picked.
                    </p>
                  </div>
                </div>

                <div className="space-y-3 max-h-[240px] overflow-auto pr-2 custom-scrollbar" data-id="element-350" data-mp="7e7f2wtco75l">
                  {items.filter(i => i.status !== 'Picked' && i.status !== 'Unavailable').map(item => <div key={item.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between" data-id="element-351" data-mp="1mug4n79y51zx">
                        <div data-id="element-352" data-mp="fjnstgejhb1l">
                          <p className="text-sm font-black text-slate-900" data-id="element-353" data-mp="fjw5p8anwgcs">
                            {item.sku}
                          </p>
                          <p className="text-[10px] font-bold text-slate-400" data-id="element-354" data-mp="5umb9lscilli">
                            {item.name}
                          </p>
                        </div>
                        <div className="text-right" data-id="element-355" data-mp="tyml6rlagug">
                          <p className="text-sm font-black text-amber-600" data-id="element-356" data-mp="3s2vrargddcyq">
                            {item.pickedQuantity} / {item.quantity}
                          </p>
                          <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest" data-id="element-357" data-mp="3j0w8puppi0to">
                            {item.status}
                          </p>
                        </div>
                      </div>)}
                </div>

                <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100" data-id="element-358" data-mp="1cayei1a0ytny">
                  <p className="text-[11px] text-amber-700 font-bold leading-relaxed" data-id="element-359" data-mp="1c9588n3bqbgh">
                    <span className="font-black uppercase tracking-wider mr-1" data-id="element-360" data-mp="1jsehmgbtya5j">
                      Note:
                    </span>
                    Proceeding will mark the order as partially fulfilled. This
                    will trigger a notification to the customer and the ordering
                    channel.
                  </p>
                </div>

                <div className="flex gap-4" data-id="element-361" data-mp="10wwrchoaad19">
                  <button onClick={() => setShowPartialModal(false)} className="flex-1 py-4 text-slate-500 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-362" data-mp="10xzhgnds8bg4">
                    Go Back
                  </button>
                  <button type="button" onClick={handleConfirmPartial} className="flex-1 py-4 bg-amber-500 text-white font-black rounded-2xl shadow-lg shadow-amber-100 hover:bg-amber-600 transition-all" data-id="element-363" data-mp="1fu6xdbi5qqpl">
                    Confirm Partial
                  </button>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Customer Confirmation Wait Modal */}
      <AnimatePresence data-id="element-364" data-mp="371g0mjkgur1n">
        {isWaitingConfirmation && <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" role="alertdialog" aria-modal="true" aria-labelledby="customer-confirmation-title" aria-describedby="customer-confirmation-description" data-id="element-365" data-mp="3b42ok9e0nrtk">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} className="w-full max-w-sm overflow-hidden rounded-[28px] bg-white shadow-2xl" data-id="element-366" data-mp="2o6hhd6l17xwm">
              <div className="p-8 text-center" data-id="element-367" data-mp="26wx23l9a7fy1">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50 text-amber-600" data-id="element-368" data-mp="2kmh58s6ryf7v">
                  <Clock className="h-8 w-8" data-id="element-369" data-mp="2fuzbop72zgwx" />
                </div>
                <h3 id="customer-confirmation-title" className="mt-5 text-xl font-black text-slate-900" data-id="element-370" data-mp="2ukohwyu16b42">
                  Waiting for Customer Confirmation
                </h3>
                <p id="customer-confirmation-description" className="mt-2 text-sm leading-6 text-slate-500" data-id="element-371" data-mp="33kjxrgyj6uaf">
                  The partial fulfillment request has been sent. This order will
                  continue automatically in 5 seconds.
                </p>
                <div className="mt-6 h-1.5 overflow-hidden rounded-full bg-amber-100" data-id="element-372" data-mp="14a21fb9w6gkl">
                  <motion.div initial={{
                width: '0%'
              }} animate={{
                width: '100%'
              }} transition={{
                duration: 5,
                ease: 'linear'
              }} className="h-full rounded-full bg-amber-500" data-id="element-373" data-mp="34c1tlylxri8x" />
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Return Receipt Confirmation Modal */}
      <AnimatePresence data-id="element-374" data-mp="v2uqidu5pae">
        {showReturnReceiptModal && <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-375" data-mp="5t4njv12d2z2">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-[32px] w-full max-w-2xl overflow-hidden shadow-2xl" data-id="element-376" data-mp="9v80ko3dsfmg">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-emerald-50/50" data-id="element-377" data-mp="1qbyewcmeq31l">
                <div className="flex items-center gap-3" data-id="element-378" data-mp="1ldn5g300xujw">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600" data-id="element-379" data-mp="2i2uqzb0n83nt">
                    <CheckCircle2 className="w-6 h-6" data-id="element-380" data-mp="2e213ujv0ysvz" />
                  </div>
                  <div data-id="element-381" data-mp="2997z3vzk3kjw">
                    <h3 className="font-bold text-slate-900" data-id="element-382" data-mp="298q412omrcdk">
                      Confirm Return Receipt
                    </h3>
                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest" data-id="element-383" data-mp="248tyv0475ewf">
                      Verify Items from Driver
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowReturnReceiptModal(false)} className="p-1 hover:bg-slate-100 rounded-full transition-colors" data-id="element-384" data-mp="2gk2wkgmp51vw">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-385" data-mp="27jehdilkgvhf" />
                </button>
              </div>

              <div className="p-8 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar" data-id="element-386" data-mp="2ci7w1eclasj6">
                <div className="space-y-4" data-id="element-387" data-mp="308265o5iy2zq">
                  {items.map(item => <div key={item.id} className={cn('p-5 rounded-2xl border-2 transition-all space-y-4', returnItemStatus[item.id]?.condition === 'Good' ? 'border-slate-100 bg-slate-50/30' : returnItemStatus[item.id]?.condition === 'Damaged' ? 'border-rose-100 bg-rose-50/30' : 'border-amber-100 bg-amber-50/30')} data-id="element-388" data-mp="30ag17x07ff7a">
                      <div className="flex items-center justify-between" data-id="element-389" data-mp="15rb18t44o0b">
                        <div className="flex items-center gap-4" data-id="element-390" data-mp="3tbo7rkvk2dp6">
                          <img src={item.image} className="w-12 h-12 rounded-xl object-cover border border-slate-200" alt="" data-id="element-391" data-mp="awvr6buiyewq" />
                          <div data-id="element-392" data-mp="3ecu8lzuxfuhn">
                            <p className="text-sm font-black text-slate-900" data-id="element-393" data-mp="3edvukzdhe1e5">
                              {item.name}
                            </p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-394" data-mp="3ocbbk9isnu9x">
                              SKU: {item.sku} • Qty: {item.quantity}
                            </p>
                          </div>
                        </div>
                        <div className="flex bg-white rounded-xl p-1 border border-slate-200 shadow-sm" data-id="element-395" data-mp="13ndtvbrc6e9m">
                          {(['Good', 'Damaged', 'Missing'] as const).map(cond => <button key={cond} onClick={() => setReturnItemStatus(prev => ({
                      ...prev,
                      [item.id]: {
                        ...prev[item.id],
                        condition: cond,
                        received: cond !== 'Missing'
                      }
                    }))} className={cn('px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all', returnItemStatus[item.id]?.condition === cond ? cond === 'Good' ? 'bg-emerald-500 text-white shadow-md shadow-emerald-100' : cond === 'Damaged' ? 'bg-rose-500 text-white shadow-md shadow-rose-100' : 'bg-amber-500 text-white shadow-md shadow-amber-100' : 'text-slate-400 hover:bg-slate-50')} data-id="element-396" data-mp="1aohegub3px2p">
                                {cond}
                              </button>)}
                        </div>
                      </div>

                      {returnItemStatus[item.id]?.condition !== 'Good' && <div className="animate-in fade-in slide-in-from-top-2 duration-200 space-y-4" data-id="element-397" data-mp="306xqg6elrfo2">
                          {returnItemStatus[item.id]?.condition === 'Missing' && <div className="space-y-2" data-id="element-398" data-mp="2r5q4mnt9w7av">
                              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-399" data-mp="iwrsle5wbans">
                                How many units are missing?
                              </label>
                              <div className="flex items-center gap-3" data-id="element-400" data-mp="8z3v4ggwnp1x">
                                <button onClick={() => setReturnItemStatus(prev => ({
                        ...prev,
                        [item.id]: {
                          ...prev[item.id],
                          missingQuantity: Math.max(0, prev[item.id].missingQuantity - 1)
                        }
                      }))} className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-lg font-bold text-slate-400 hover:bg-slate-50" data-id="element-401" data-mp="dxf3emrjxri1">
                                  -
                                </button>
                                <div className="w-16 text-center font-black text-lg text-slate-900" data-id="element-402" data-mp="37rb71l393d6u">
                                  {returnItemStatus[item.id]?.missingQuantity || 0}
                                </div>
                                <button onClick={() => setReturnItemStatus(prev => ({
                        ...prev,
                        [item.id]: {
                          ...prev[item.id],
                          missingQuantity: Math.min(item.quantity, prev[item.id].missingQuantity + 1)
                        }
                      }))} className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-lg font-bold text-slate-400 hover:bg-slate-50" data-id="element-403" data-mp="3cpuszlhugoz7">
                                  +
                                </button>
                                <span className="text-xs text-slate-400 font-bold" data-id="element-404" data-mp="1biaszq20785c">
                                  / {item.quantity} units total
                                </span>
                              </div>
                            </div>}
                          <textarea placeholder={`Describe the ${returnItemStatus[item.id]?.condition.toLowerCase()} issue...`} value={returnItemStatus[item.id]?.note} onChange={e => setReturnItemStatus(prev => ({
                    ...prev,
                    [item.id]: {
                      ...prev[item.id],
                      note: e.target.value
                    }
                  }))} className="w-full p-3 bg-white border border-slate-200 rounded-xl text-xs outline-none focus:border-blue-500 transition-all min-h-[60px]" data-id="element-405" data-mp="1gbdssfxdjdtz" />
                        </div>}
                    </div>)}
                </div>
              </div>

              <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-4" data-id="element-406" data-mp="1l7hrqose6cet">
                <button onClick={() => setShowReturnReceiptModal(false)} className="flex-1 py-4 text-slate-500 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-407" data-mp="177zy9w45iaz4">
                  Cancel
                </button>
                <button onClick={() => {
              onUpdateStatus('Return Requested');
              setShowReturnReceiptModal(false);
              import('sonner').then(({
                toast
              }) => toast.success('Return receipt confirmed. Status updated to Return Requested.'));
            }} className="flex-[2] py-4 bg-emerald-600 text-white font-black rounded-2xl shadow-lg shadow-emerald-100 hover:bg-emerald-700 transition-all" data-id="element-408" data-mp="3dybq2vjczke6">
                  Confirm Receipt & Proceed
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Return Modal */}
      <AnimatePresence data-id="element-409" data-mp="2cgui4hgu2tcq">
        {showReturnModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-410" data-mp="27fn9yhas3s5j">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl" data-id="element-411" data-mp="mz71792xibto">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-rose-50/50" data-id="element-412" data-mp="15o3vhcybt4bw">
                <div className="flex items-center gap-3" data-id="element-413" data-mp="1alvokaapiwho">
                  <ArrowLeft className="w-6 h-6 text-rose-600" data-id="element-414" data-mp="1fmjq3pwkavq5" />
                  <h3 className="font-bold text-slate-900" data-id="element-415" data-mp="1joeoalj74szp">Return Process</h3>
                </div>
                <button onClick={() => setShowReturnModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-416" data-mp="3he66yymqpa8w">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-417" data-mp="3lg1j6y67l81g" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-418" data-mp="2q3s1bhdjc4h4">
                <div className="grid grid-cols-2 gap-6" data-id="element-419" data-mp="2m4j6082ujyjg">
                  <div className="space-y-2" data-id="element-420" data-mp="2m62l3vcro3jn">
                    <label className="text-xs font-bold text-slate-500 uppercase" data-id="element-421" data-mp="3040yoyj9jvy3">
                      Return Reason
                    </label>
                    <select value={returnReason} onChange={e => setReturnReason(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl text-sm outline-none" data-id="element-422" data-mp="2v2ldn6h35pfp">
                      <option value="" data-id="element-423" data-mp="2h6hpnuwt2dbo">Select a reason...</option>
                      <option value="Damaged" data-id="element-424" data-mp="2cdwtimqffo4z">Damaged Item</option>
                      <option value="Wrong" data-id="element-425" data-mp="1oom1pur2142z">Wrong Item Received</option>
                      <option value="Expired" data-id="element-426" data-mp="1omrjio763q2l">Expired Product</option>
                      <option value="Not Needed" data-id="element-427" data-mp="1johhpuqxtr80">No longer needed</option>
                    </select>
                  </div>
                  <div className="space-y-2" data-id="element-428" data-mp="3vbjr5lxlm4al">
                    <label className="text-xs font-bold text-slate-500 uppercase" data-id="element-429" data-mp="3yqgfbb5xigx">
                      Return Method
                    </label>
                    <div className="grid grid-cols-2 gap-2" data-id="element-430" data-mp="3me10o216qxrb">
                      <button onClick={() => setReturnMethod('Driver')} className={cn('py-3 rounded-xl border-2 text-xs font-bold transition-all', returnMethod === 'Driver' ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-slate-100 text-slate-500 hover:border-slate-200')} data-id="element-431" data-mp="3qd039fzg2ifl">
                        Request Driver
                      </button>
                      <button onClick={() => setReturnMethod('Customer')} className={cn('py-3 rounded-xl border-2 text-xs font-bold transition-all', returnMethod === 'Customer' ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-slate-100 text-slate-500 hover:border-slate-200')} data-id="element-432" data-mp="2tpvf5d2b54qf">
                        Customer Return
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-433" data-mp="gg9m5ih4qnt6">
                  {items.map(item => <div key={item.id} className="p-4 bg-slate-50 rounded-xl flex items-center justify-between" data-id="element-434" data-mp="bhg6pkia8mqd">
                      <div className="flex items-center gap-3" data-id="element-435" data-mp="7ih4i7nxr42m">
                        <img src={item.image} className="w-10 h-10 rounded-lg" data-id="element-436" data-mp="25ufuw20qap99" />
                        <p className="text-sm font-bold" data-id="element-437" data-mp="3js0j5oq803om">{item.name}</p>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-438" data-mp="3oqlog2kbxdjx">
                        <button className="w-8 h-8 rounded border border-slate-200" data-id="element-439" data-mp="3ooqzmd5xt1ak">
                          -
                        </button>
                        <span className="text-sm font-bold" data-id="element-440" data-mp="1eay50xgivif">0</span>
                        <button className="w-8 h-8 rounded border border-slate-200" data-id="element-441" data-mp="1fly0pns7j49">
                          +
                        </button>
                        <span className="text-xs text-slate-400" data-id="element-442" data-mp="35t0uwi6mdqsz">
                          / {item.quantity}
                        </span>
                      </div>
                    </div>)}
                </div>
                <button disabled={!returnReason} onClick={() => {
              const nextStatus = returnMethod === 'Driver' ? 'Waiting Return Confirmation' : 'Return Requested';
              onUpdateStatus(nextStatus);
              setShowReturnModal(false);
              import('sonner').then(({
                toast
              }) => toast.success(returnMethod === 'Driver' ? 'Driver requested for return. Waiting for item arrival.' : 'Return initiated. Please scan items when received.'));
            }} className="w-full py-4 bg-rose-600 text-white font-bold rounded-2xl shadow-lg disabled:opacity-50" data-id="element-443" data-mp="3etz033sso4d5">
                  Confirm Return ({returnMethod})
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Rx Modal */}
      <AnimatePresence data-id="element-444" data-mp="2z8xn9e705he3">
        {showRxModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-445" data-mp="2v7c4xdbp8awi">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl" data-id="element-446" data-mp="2l2nfb0xe84lq">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-purple-50/50" data-id="element-447" data-mp="21dcxnhe379kz">
                <h3 className="font-bold text-slate-900" data-id="element-448" data-mp="26faq928dngql">
                  Prescription Attachment
                </h3>
                <button onClick={() => setShowRxModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-449" data-mp="1sgkrli5wt904">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-450" data-mp="3msshgh3dizp1" />
                </button>
              </div>
              <div className="p-8" data-id="element-451" data-mp="3rqkjw51na47h">
                <div className="aspect-[4/3] bg-slate-100 rounded-2xl border-2 border-slate-200 overflow-hidden" data-id="element-452" data-mp="xdlym177j7r7">
                  {order.prescriptionImage ? <img src={order.prescriptionImage} alt="Rx" className="w-full h-full object-contain" data-id="element-453" data-mp="12cylgly2wets" /> : <div className="flex flex-col items-center justify-center h-full text-slate-400" data-id="element-454" data-mp="1c4bg1lb9hj3i">
                      <Globe className="w-12 h-12 mb-2" data-id="element-455" data-mp="1g6g484ixxnir" />
                      <p className="font-bold" data-id="element-456" data-mp="1l1vgwt57pddl">No Image Attached</p>
                    </div>}
                </div>
                <div className="mt-6 flex gap-3" data-id="element-457" data-mp="1q06vfh6gcc25">
                  <button onClick={() => setShowRxModal(false)} className="flex-1 py-4 bg-white text-slate-600 font-bold border border-slate-200 rounded-2xl" data-id="element-458" data-mp="1v0b9z1nsuwpq">
                    Close
                  </button>
                  <button onClick={() => {
                setShowRxModal(false);
                setShowHoldModal(true);
                setHoldReason('Prescription Not Valid');
              }} className="flex-1 py-4 bg-amber-500 text-white font-bold rounded-2xl shadow-lg flex items-center justify-center gap-2" data-id="element-459" data-mp="2dxcuk782sjzd">
                    <Clock className="w-4 h-4" data-id="element-460" data-mp="2ww79bg7jhkfj" />
                    Hold: Invalid Rx
                  </button>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Hold Modal */}
      <AnimatePresence data-id="element-461" data-mp="2nu7qcicys69y">
        {showHoldModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-462" data-mp="2iwy2h75842o8">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-463" data-mp="1cgvsqp7ssjw8">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-464" data-mp="zx8bpf1lrd9o">
                <h3 className="font-bold text-slate-900" data-id="element-465" data-mp="14vrxnfg74p21">Hold Order</h3>
                <button onClick={() => setShowHoldModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-466" data-mp="q7ousexg9thg">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-467" data-mp="v0rqaslsfd0a" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-468" data-mp="l93ru7qtcvpb">
                <div className="space-y-2" data-id="element-469" data-mp="l9x05d5f7t9k">
                  {['Customer Unreachable', 'Incorrect Address', 'Prescription Not Valid', 'Other'].map(reason => <button key={reason} onClick={() => setHoldReason(reason)} className={cn('w-full px-4 py-3 rounded-xl border-2 text-left text-sm font-medium transition-all', holdReason === reason ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200')} data-id="element-470" data-mp="3orrws4tgqe1p">
                      {reason}
                    </button>)}
                </div>
                <button disabled={!holdReason} onClick={() => {
              onUpdateStatus('Held', {
                holdReason
              });
              setShowHoldModal(false);
            }} className="w-full py-4 bg-slate-800 text-white font-bold rounded-2xl shadow-lg disabled:opacity-50" data-id="element-471" data-mp="l9zvfztfshqu">
                  Confirm Hold
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* OTP Modal */}
      <AnimatePresence data-id="element-472" data-mp="2tiej01zy2bkl">
        {showOtpModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-473" data-mp="2tl0yqowznxdw">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-474" data-mp="20u8v56dlg6gj">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-475" data-mp="3m84vy7pub7f4">
                <h3 className="font-bold text-slate-900" data-id="element-476" data-mp="3q8nrocuusxml">OTP Verification</h3>
                <button onClick={() => setShowOtpModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-477" data-mp="3qb1i28el9lnt">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-478" data-mp="1akry83dvfpwe" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-479" data-mp="15rp49u56mefo">
                <div className="rounded-2xl border border-purple-100 bg-purple-50 p-4 text-center" data-id="element-480" data-mp="10uyn13hn5cso">
                  <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-white text-purple-600 shadow-sm" data-id="element-481" data-mp="vvvirl9hjalr">
                    <User className="h-5 w-5" data-id="element-482" data-mp="qupohj06ffa4" />
                  </div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-purple-600" data-id="element-483" data-mp="mtw1cruk64ia">
                    Self Pickup
                  </p>
                  <p className="mt-1 text-sm font-bold text-slate-900" data-id="element-484" data-mp="hxxgvfccp6n5">
                    {order.customerName} is collecting this order
                  </p>
                </div>
                <div className="text-center space-y-2" data-id="element-485" data-mp="2cadiu3y45ym7">
                  <Smartphone className="w-8 h-8 text-blue-600 mx-auto" data-id="element-486" data-mp="2vdyw20bf530l" />
                  <h4 className="font-bold text-lg" data-id="element-487" data-mp="304xfh8rpeh82">Confirm Pickup with OTP</h4>
                  <p className="text-sm text-slate-500" data-id="element-488" data-mp="3071t4ifemulj">
                    Ask the customer for the 4-digit collection code sent to
                    {` ${order.customerPhone || ' their phone'}`}.
                  </p>
                </div>
                <input autoFocus inputMode="numeric" type="text" maxLength={4} value={otpValue} onChange={e => setOtpValue(e.target.value.replace(/\D/g, ''))} aria-label="Four digit pickup OTP" className="w-full text-center py-6 bg-slate-50 border-2 border-slate-100 rounded-2xl font-black text-4xl tracking-widest outline-none focus:border-blue-500" data-id="element-489" data-mp="3f1y7lddt4e72" />
                <button disabled={otpValue.length !== 4} onClick={() => {
              if (otpValue === order.otp) {
                onUpdateStatus('Delivered', {
                  otpVerified: true
                });
                setShowOtpModal(false);
              } else {
                import('sonner').then(({
                  toast
                }) => toast.error('Invalid OTP'));
              }
            }} className="w-full py-4 bg-blue-600 text-white font-bold rounded-2xl shadow-lg disabled:opacity-50" data-id="element-490" data-mp="1ylvbf0n7tz6n">
                  Verify & Complete
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}